/* =========================================================
   Session, update window, per-screen variants, toasts.
   Toasts are always replicated into the bell queue (with the
   originating section), which is what the notification centre
   reads back.
   ========================================================= */
import { create } from 'zustand';
import type { DemoUser, Role, Toast, ToastInput, WindowState } from '@/types';
import { IS_API, ds } from '@/data';
import { DEMO_USERS } from '@/data/seed';
import { readJSON, readString, remove, writeJSON, writeString } from '@/lib/storage';
import { endSession } from '@/lib/session';

const K_USER = 'etc.user';
const K_ROLE = 'etc.role';
const K_WINDOW = 'etc.window';

const DEFAULT_WINDOW: WindowState = { open: true, deadline: '2026-06-28T23:59:59' };

/** Per-screen layout variants.
 *  Site Manager cannot use view A on the dashboard; the Site and Org
 *  panels always open in their tabbed view. */
export interface VariantMap {
  dashboard: string;
  org: string;
  site: string;
  reports: string;
}

function initialVariant(role: Role): VariantMap {
  return role === 'SITE_MGR'
    ? { dashboard: 'B', org: 'C', site: 'A', reports: 'A' }
    : { dashboard: 'A', org: 'C', site: 'A', reports: 'A' };
}

/** Section label used as the origin of a notification raised on this screen. */
const ORIGIN: Record<string, [string, string]> = {
  dashboard: ['Dashboard', 'Dashboard'],
  org: ['Organizaciones', 'Organizations'],
  sites: ['Sites', 'Sites'],
  site: ['Sites', 'Sites'],
  instructors: ['Instructores', 'Instructors'],
  instructorDetail: ['Instructores', 'Instructors'],
  users: ['Usuarios y roles', 'Users & roles'],
  reports: ['Reportes', 'Reports'],
  admin: ['Administración', 'Administration'],
  login: ['Acceso', 'Sign-in'],
};

interface AppState {
  user: DemoUser | null;
  role: Role;
  loggingIn: boolean;
  loggingOut: boolean;
  windowState: WindowState;
  variant: VariantMap;
  toasts: Toast[];
  /** Bumped whenever the bell queue changes, so subscribers re-render. */
  notifVersion: number;
  /** Current screen id, used to stamp a notification's origin. */
  screen: string;
  /** Language, mirrored from i18n so non-React callers can read it. */
  lang: 'es' | 'en';

  setScreen: (screen: string) => void;
  setLang: (lang: 'es' | 'en') => void;
  beginLogin: (u: DemoUser, done: () => void) => void;
  beginLogout: (done: () => void) => void;
  /** Cierra la sesión sin la pantalla de transición: la usa el arranque
   *  cuando la sesión guardada ya no es válida. */
  dropSession: () => void;
  /** Aplica la identidad que acaba de confirmar el servidor tras una
   *  recarga. Sustituye a lo guardado en el navegador, que es una copia
   *  editable por su dueño. */
  adoptSession: (u: DemoUser) => void;
  setRole: (role: Role) => void;
  setWindowState: (w: WindowState) => void;
  setVariantFor: (key: keyof VariantMap, v: string) => void;
  /** Devuelve el id del aviso, para poder retirarlo antes de tiempo. */
  addToast: (t: ToastInput) => string;
  dismissToast: (id: string) => void;
  /** Retira los avisos de éxito que siguen en pantalla. Se usa cuando el
   *  servidor rechaza un guardado que la interfaz ya había dado por bueno:
   *  dejar «Cambios guardados» al lado de «No se guardó» es peor que no
   *  avisar de nada. Los errores se conservan. */
  retractSuccessToasts: () => void;
  bumpNotifications: () => void;
}

/** Login and logout both run behind a 4s branded loading screen. */
const TRANSITION_MS = 4000;

export const useAppStore = create<AppState>((set, get) => ({
  user: readJSON<DemoUser | null>(K_USER, null),
  role: ((): Role => {
    const stored = readString(K_ROLE) as Role | null;
    if (stored === 'ETC_ADMIN' || stored === 'PARTNER_MGR' || stored === 'SITE_MGR') return stored;
    const u = readJSON<DemoUser | null>(K_USER, null);
    return u ? u.role : 'PARTNER_MGR';
  })(),
  loggingIn: false,
  loggingOut: false,
  // en modo API es un valor de relleno: la hidratación lo sustituye por el
  // de la base antes de pintar nada
  windowState: IS_API ? DEFAULT_WINDOW : (readJSON<WindowState>(K_WINDOW, DEFAULT_WINDOW) || DEFAULT_WINDOW),
  variant: initialVariant('PARTNER_MGR'),
  toasts: [],
  notifVersion: 0,
  screen: 'dashboard',
  lang: 'es',

  setScreen: (screen) => set({ screen }),
  setLang: (lang) => set({ lang }),

  beginLogin: (u, done) => {
    set({ loggingIn: true });
    window.setTimeout(() => {
      writeJSON(K_USER, u);
      writeString(K_ROLE, u.role);
      set({ user: u, role: u.role, loggingIn: false, variant: initialVariant(u.role) });
      done();
    }, TRANSITION_MS);
  },

  beginLogout: (done) => {
    set({ loggingOut: true });
    // con backend real, además se invalida la sesión en el servidor
    void endSession();
    window.setTimeout(() => {
      remove(K_USER);
      set({ user: null, loggingOut: false });
      done();
    }, TRANSITION_MS);
  },

  dropSession: () => {
    remove(K_USER);
    set({ user: null, loggingIn: false, loggingOut: false });
  },

  adoptSession: (u) => {
    writeJSON(K_USER, u);
    writeString(K_ROLE, u.role);
    set({ user: u, role: u.role, variant: initialVariant(u.role) });
  },

  /** Demo affordance: switching role swaps in the matching demo identity. */
  setRole: (role) => {
    // Con backend real el rol lo decide la cuenta, no el navegador. Se
    // ignora aquí y no solo se esconde el menú: la llamada sigue siendo
    // alcanzable desde la consola del navegador, y aunque el servidor
    // autoriza por el token —así que cambiarlo no da acceso a ningún
    // dato ajeno— sí abriría pantallas de administración que ese usuario
    // no debe ni ver.
    if (IS_API) return;
    writeString(K_ROLE, role);
    const next = DEMO_USERS.find((u) => u.role === role);
    const cur = get().user;
    const user = next && (!cur || next.username !== cur.username) ? next : cur;
    if (user && user !== cur) writeJSON(K_USER, user);

    // Site Manager has no view A anywhere except the Site and Reports panels.
    set((s) => {
      const v = { ...s.variant };
      if (role === 'SITE_MGR') {
        (Object.keys(v) as (keyof VariantMap)[]).forEach((k) => {
          if (k === 'site' || k === 'reports') return;
          if (v[k] === 'A') v[k] = 'B';
        });
      }
      return { role, user: user ?? s.user, variant: v };
    });
  },

  setWindowState: (w) => {
    // Contra el backend real la ventana la manda la base, no el navegador:
    // guardarla aquí solo serviría para que una copia vieja parpadeara al
    // recargar, o peor, para que dos administradores vieran cosas distintas.
    if (!IS_API) writeJSON(K_WINDOW, w);
    set({ windowState: w });
  },

  setVariantFor: (key, v) => set((s) => ({ variant: { ...s.variant, [key]: v } })),

  addToast: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));

    // En modo demostración la campana repite lo que uno mismo acaba de
    // hacer, porque no hay nada más que enseñar. Con servidor detrás, la
    // cola son los avisos que manda OTRA persona y los trae la API: meter
    // aquí un eco local los mezclaría, y ese eco desaparecería al recargar.
    if (IS_API) {
      set((s) => ({ notifVersion: s.notifVersion + 1 }));
      // Los persistentes esperan a que los cierren: son los que hay que
      // leer, no los que solo confirman que algo pasó.
      if (!t.persistente) {
        window.setTimeout(() => {
          set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
        }, 4000);
      }
      return id;
    }

    // replicate into the bell queue, stamped with its origin
    const { screen, lang } = get();
    const fallback = ORIGIN[screen] ? ORIGIN[screen][lang === 'es' ? 0 : 1] : 'ETC Connect';
    ds.addNotification({
      kind: t.kind ?? 'ok',
      title: t.title,
      body: t.body || '',
      origin: t.origin || fallback,
      details: t.details || '',
      target: t.target || null,
      targetLabel: t.targetLabel || '',
    });
    set((s) => ({ notifVersion: s.notifVersion + 1 }));

    window.setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
    }, 4000);
    return id;
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  retractSuccessToasts: () =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.kind === 'err') })),

  bumpNotifications: () => set((s) => ({ notifVersion: s.notifVersion + 1 })),
}));

/** Non-React access, for helpers that raise toasts outside a component. */
export const addToast = (t: ToastInput) => useAppStore.getState().addToast(t);
